import React, { useMemo, useState } from 'react';
import { Drawer, Empty, Input, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { WorkspaceApplication, WorkspaceRoute, WorkspaceTask } from '../../store/api/workspaceApi';
import {
  ATTENTION_INBOX_CATEGORY_LABELS,
  filterAttentionSignalsByQuery,
  type AttentionInboxCategory,
  type AttentionSignal,
} from '../../domain/workspaceAttentionInbox';
import { AttentionInboxSignalRow } from './attentionInboxRows';

const { Text } = Typography;

export interface AttentionCategoryDrawerProps {
  open: boolean;
  category: AttentionInboxCategory | null;
  signals: AttentionSignal[];
  signalCount: number;
  closureBlockedEntities: Set<string>;
  routes: WorkspaceRoute[];
  applications: WorkspaceApplication[];
  onClose: () => void;
  onOpenFinanceContext: (p: { kind: 'route' | 'application'; id: number }) => void;
  onTaskAction: (task: WorkspaceTask) => void;
}

export const AttentionCategoryDrawer: React.FC<AttentionCategoryDrawerProps> = ({
  open,
  category,
  signals,
  signalCount,
  closureBlockedEntities,
  routes,
  applications,
  onClose,
  onOpenFinanceContext,
  onTaskAction,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const drawerWidth =
    typeof window !== 'undefined' ? Math.min(720, window.innerWidth - 24) : 720;

  const categoryLabel = category ? ATTENTION_INBOX_CATEGORY_LABELS[category] : '';

  const filteredSignals = useMemo(
    () => filterAttentionSignalsByQuery(signals, searchQuery, routes, applications),
    [signals, searchQuery, routes, applications]
  );

  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  return (
    <Drawer
      title={
        category ? (
          <span>
            {categoryLabel} ({signalCount})
          </span>
        ) : (
          ''
        )
      }
      placement="right"
      width={drawerWidth}
      open={open}
      onClose={handleClose}
      destroyOnClose
      styles={{
        body: {
          padding: '12px 16px',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        },
      }}
    >
      <Input
        allowClear
        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
        placeholder="Поиск: маршрут, заявка, клиент, перевозчик"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{ marginBottom: 12, flexShrink: 0 }}
      />
      {searchQuery.trim() ? (
        <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block', flexShrink: 0 }}>
          Найдено: {filteredSignals.length} из {signalCount}
        </Text>
      ) : null}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {filteredSignals.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={searchQuery.trim() ? 'Ничего не найдено' : 'Нет сигналов'}
            style={{ marginTop: 24 }}
          />
        ) : (
          filteredSignals.map((signal) => {
            const entityKey = `${signal.entity.kind}:${signal.entity.id}`;
            return (
              <AttentionInboxSignalRow
                key={signal.id}
                signal={signal}
                closureBlocked={closureBlockedEntities.has(entityKey)}
                applications={applications}
                onOpenFinanceContext={onOpenFinanceContext}
                onTaskAction={onTaskAction}
              />
            );
          })
        )}
      </div>
    </Drawer>
  );
};

export default AttentionCategoryDrawer;
